import { useMemo } from "react";
import DOMPurify from "dompurify";
import { cn } from "@/modern/utils";

interface HTMLRendererProps {
  content: string;
  className?: string;
}

export const HTMLRenderer = ({ content, className }: HTMLRendererProps) => {
  // Sanitize HTML content to prevent XSS attacks
  const sanitizedHtml = useMemo(() => {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins', 'mark',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'a', 'code', 'pre',
        'blockquote',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span', 'b', 'i'
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style']
    });
  }, [content]);

  return (
    <div
      className={cn(
        "text-sm prose prose-sm max-w-none text-card-foreground",
        "prose-slate dark:prose-invert",
        // Customize prose styling for HTML content
        "prose-headings:font-semibold prose-headings:mb-2",
        "prose-p:my-1.5 prose-p:leading-relaxed",
        "prose-a:text-primary prose-a:no-underline hover:prose-a:underline",
        "prose-strong:font-semibold",
        "prose-mark:bg-primary/30 prose-mark:text-foreground",
        "selection:bg-primary/35 selection:text-foreground",
        "prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs",
        "prose-pre:bg-muted prose-pre:border prose-pre:border-border",
        "prose-ul:my-2 prose-li:my-0.5",
        "prose-ol:my-2",
        "prose-blockquote:border-l-primary prose-blockquote:italic",
        "prose-table:text-sm",
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};
